import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  LocalShipping as ShipmentIcon,
  NotificationsActive as ReminderIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";

interface EmailTestSetSummary {
  id: string;
  name: string;
  description: string | null;
  emailCount: number;
  shipmentCount: number;
  reminderCount: number;
  inputCount: number;
  createdAt: string;
  updatedAt: string;
}

export function EmailTestSetsPage() {
  const navigate = useNavigate();
  const [testSets, setTestSets] = useState<EmailTestSetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form states
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [selectedTestSet, setSelectedTestSet] =
    useState<EmailTestSetSummary | null>(null);

  useEffect(() => {
    fetchTestSets();
  }, []);

  const fetchTestSets = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ testSets: EmailTestSetSummary[] }>(
        `/admin/email-test-sets/list`,
        {}
      );
      setTestSets(res.testSets);
    } catch (error) {
      console.error("Error fetching test sets:", error);
      setError("Failed to load test sets");
    } finally {
      setLoading(false);
    }
  };

  const handleViewTestSet = (testSet: EmailTestSetSummary) => {
    navigate(`/admin/test-sets/${testSet.id}`);
  };

  const handleCreateTestSet = async () => {
    try {
      setError(null);
      const res = await apiClient.post<{ testSet: EmailTestSetSummary }>(
        `/admin/email-test-sets/create`,
        {
          name: newName.trim(),
          description: newDescription.trim() || undefined,
        }
      );
      setTestSets([res.testSet, ...testSets]);
      setCreateDialogOpen(false);
      setNewName("");
      setNewDescription("");
      showSnackbar("Test set created successfully", 3000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to create test set";
      setError(errorMessage);
    }
  };

  const handleUpdateTestSet = async () => {
    if (!selectedTestSet) return;

    try {
      setError(null);
      const res = await apiClient.put<{ testSet: EmailTestSetSummary }>(
        `/admin/email-test-sets/update`,
        {
          id: selectedTestSet.id,
          name: editName.trim(),
          description: editDescription.trim() || undefined,
        }
      );
      setTestSets(
        testSets.map((ts) => (ts.id === res.testSet.id ? res.testSet : ts))
      );
      setEditDialogOpen(false);
      setSelectedTestSet(null);
      showSnackbar("Test set updated successfully", 3000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to update test set";
      setError(errorMessage);
    }
  };

  const handleDeleteTestSet = async () => {
    if (!selectedTestSet) return;

    try {
      setError(null);
      await apiClient.delete(`/admin/email-test-sets/delete`, {
        id: selectedTestSet.id,
      });
      setTestSets(testSets.filter((ts) => ts.id !== selectedTestSet.id));
      setDeleteConfirmOpen(false);
      setSelectedTestSet(null);
      showSnackbar("Test set deleted successfully", 3000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to delete test set";
      setError(errorMessage);
    }
  };

  const openEditDialog = (
    testSet: EmailTestSetSummary,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setSelectedTestSet(testSet);
    setEditName(testSet.name);
    setEditDescription(testSet.description || "");
    setEditDialogOpen(true);
  };

  const openDeleteConfirm = (
    testSet: EmailTestSetSummary,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setSelectedTestSet(testSet);
    setDeleteConfirmOpen(true);
  };

  if (loading) {
    return (
      <AdminPage title="Test Sets">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Test Sets">
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Test Sets</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Test Set
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Inputs</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testSets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No test sets yet. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                testSets.map((testSet) => (
                  <TableRow
                    key={testSet.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleViewTestSet(testSet)}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{testSet.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="text.secondary"
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {testSet.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                        {testSet.emailCount > 0 && (
                          <Chip
                            icon={<EmailIcon />}
                            label={testSet.emailCount}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {testSet.shipmentCount > 0 && (
                          <Chip
                            icon={<ShipmentIcon />}
                            label={testSet.shipmentCount}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {testSet.reminderCount > 0 && (
                          <Chip
                            icon={<ReminderIcon />}
                            label={testSet.reminderCount}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {testSet.inputCount === 0 && (
                          <Chip label="0" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(testSet.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => openEditDialog(testSet, e)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => openDeleteConfirm(testSet, e)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Test Set</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              sx={{ mt: 2 }}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateTestSet}
              disabled={!newName.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Test Set</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              sx={{ mt: 2 }}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpdateTestSet}
              disabled={!editName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete Test Set</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedTestSet?.name}"? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteTestSet}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
